import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { MapPin } from 'lucide-react';

import tableIconUserShare from '../../../assets/images/tabler-icon-user-share.svg';
import JobSection from '../../../containers/Browse/OpeningsListElement/JobSection';
import { capitalizeWords } from '../../../helpers/commonHelper';
import urls from '../../../routing/urls';
import ButtonWithIcon from '../../shared/ButtonWithIcon/ButtonWithIcon';

import styles from '../../../containers/Browse/OpeningsListElement/OpeningsListElement.module.scss';

export const RecommendationItem = ({ recommendation }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const handleReferClick = () => {
    if (recommendation.job_referral_link) {
      window.open(`${recommendation.job_referral_link}&c_id=${recommendation.contact_id}`, '_blank');
    } else {
      localStorage.setItem('activeTab', 'browse');
      navigate(`${urls.jobDetail.replace(':jobId', recommendation.job_uuid)}`);
    }
  };

  const handleNavigation = (path) => {
    localStorage.setItem('activeTab', '/account/profile');
    navigate(path);
  };

  const handleJobClick = () => {
    localStorage.setItem('activeTab', 'browse');
    navigate(`${urls.jobDetail.replace(':jobId', recommendation.job_uuid)}`);
  };

  return (
    <div className="rounded-lg border border-gray-200">
      <div className="flex flex-row sm:flex-col gap-[12px] p-[12px] pb-0">
        {/* Column 1: Profile picture */}
        <div className="flex gap-3">
          <div
            className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 text-base font-medium cursor-pointer"
            onClick={() => handleNavigation('/account/network')}
            onKeyDown={(e) => e.key === 'Enter' && handleNavigation('/account/network')}
            tabIndex={0}
            role="button"
          >
            {recommendation?.person_name
              ? recommendation.person_name
                  .split(' ')
                  .map((name) => name[0])
                  .join('')
                  .substring(0, 2)
                  .toUpperCase()
              : 'U'}
          </div>
        </div>

        {/* Column 2: Person details */}
        <div className="flex-grow min-w-0">
          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <div className="flex flex-col gap-2 flex-grow min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <span
                  className="hover:underline truncate"
                  onClick={() => handleNavigation('/account/network')}
                  onKeyDown={(e) => e.key === 'Enter' && handleNavigation('/account/network')}
                  tabIndex={0}
                  role="button"
                >
                  <span className="text-sm font-semibold text-gray-900">
                    {capitalizeWords(recommendation.person_name)}
                  </span>
                </span>
                <span className="px-2 py-1 bg-[#D5F3DF] text-emerald-700 rounded-full text-xs font-medium whitespace-nowrap w-fit">
                  {t('matchScore', { score: Math.floor(recommendation.match_score * 100) })}
                </span>
              </div>
              <div className="flex flex-col gap-2">
                <p className="text-sm font-normal text-gray-600 truncate mb-0">
                  {recommendation.contact_job_title}
                  {recommendation.contact_company && ` @ ${recommendation.contact_company}`}
                </p>
                {recommendation.contact_location && (
                  <div className="text-sm text-gray-900 truncate font-normal flex items-center gap-1">
                    <MapPin className="w-3 h-3 inline-block" />
                    {recommendation.contact_location}
                  </div>
                )}
                {recommendation.matching_reason && (
                  <div className="mt-1 rounded-md bg-purple-50 border border-purple-100 px-3 py-2">
                    <p className="text-xs font-semibold text-purple-700 mb-0.5">{t('matchingReason.title')}</p>
                    <p className="text-xs text-purple-600 leading-relaxed mb-0">{recommendation.matching_reason}</p>
                  </div>
                )}
                <div className="block sm:hidden w-full">
                  <ButtonWithIcon
                    text={t('buttons.refer')}
                    img={tableIconUserShare}
                    imgClassName="btn-icon-36"
                    textClassName="btn-icon-text-tertiary-40"
                    variant="tertiary-36"
                    onClick={handleReferClick}
                    className="w-full"
                  />
                </div>
              </div>
            </div>
            <div className="hidden sm:flex sm:items-start flex-shrink-0">
              <ButtonWithIcon
                text={t('buttons.refer')}
                img={tableIconUserShare}
                imgClassName="btn-icon-36"
                textClassName="btn-icon-text-tertiary-40"
                variant="tertiary-36"
                onClick={handleReferClick}
              />
            </div>
          </div>
          <hr className="hidden sm:block border-t border-gray-400 mt-2 mb-1" />
        </div>
      </div>

      <div className={classNames(styles.recommendationItem)} role="presentation" onClick={handleJobClick}>
        <JobSection key={recommendation.job_uuid} job={recommendation.job_data} shouldShowButtons={false} />
      </div>
    </div>
  );
};
